import * as React from "react";
import { observer, inject } from "mobx-react";
import { Link, withRouter, RouteComponentProps, RouteProps } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import Select from "react-select";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faPenSquare, faArchive, faTrashAlt, faBoxOpen, faFlag } from "@fortawesome/free-solid-svg-icons";

import NewCustomerDialog from "./NewCustomerDialog";
import { CustomerLink } from "../common/CustomerLink";

import LoadingWidget from "../common/LoadingWidget";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";

import { CustomerListStore } from "../../stores/CustomerListStore";
import { ConfigurationStore } from "../../stores/ConfigurationStore";

import { SimpleCustomer, Business, ExtractFriendlyName, Contact } from "../../model/Customer";
import { successMessage, errorMessage } from "../common/Toast";
import { worker } from "cluster";

export interface OptionType {
    value: string;
    label: string;
}

export interface Props extends RouteComponentProps<any> {
    customerListStore?: CustomerListStore;
    configurationStore?: ConfigurationStore;
}

export interface State {
    isLoading: boolean;
    showArchived: boolean;
}

@inject("customerListStore")
@inject("configurationStore")
@observer
export class CustomerList extends React.Component<Props, State> {
    private NewCustomerDialog: NewCustomerDialog | null = null;

    constructor(props: Props) {
        super(props);
        this.state = {
            isLoading: false,
            showArchived: false
        };
    }

    handleDialogCustomer = (): void => {
        if (this.NewCustomerDialog) {
            this.NewCustomerDialog.toggle();
        }
    };

    handleSearchOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.preventDefault();
        this.props.customerListStore!.updateCustomerListFilters({ fullTextSearch: e.target.value });
    };

    onChangeSelectRole = (e: Array<OptionType>): void => {
        const selectedRoles = e.map((role: any) => role.value);
        this.props.customerListStore!.updateCustomerListFilters({ selectedRoles });
    };

    onChangeSelectSale = (e: Array<OptionType>): void => {
        const selectedSales = e.map((sale: any) => sale.value);
        this.props.customerListStore!.updateCustomerListFilters({ selectedSales });
    };

    tableChanged = (type: TableChangeType, event: TableChangeNewState) => {
        if (type === "pagination") {
            this.props.customerListStore!.updateCustomerListState({
                page: event.page,
                sizePerPage: event.sizePerPage
            });
        } else if (type === "sort") {
            this.props.customerListStore!.updateCustomerListState({
                sortOrder: event.sortOrder,
                sortField: event.sortField as any
            });
        }
    };

    deleteCustomer = (customer: SimpleCustomer) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        event.stopPropagation();

        const goAhead = await SimpleConfirmation(`Are you sure you'd like to delete ${ExtractFriendlyName(customer)}?`);
        if (!goAhead) {
            return;
        }

        try {
            await this.props.customerListStore!.deleteCustomer(customer.id);
            successMessage(`${ExtractFriendlyName(customer)} has been deleted.`);
        } catch (error) {
            errorMessage(error);
        }
    };

    handleArchiveCustomer = (customer: SimpleCustomer, value: boolean) => async (event: React.MouseEvent<any>) => {
        event.preventDefault();
        event.stopPropagation();

        try {
            await this.props.customerListStore!.updateArchivedStatus(customer.id, value);
            successMessage(`${ExtractFriendlyName(customer)} has been ${value ? `archived` : `unarchived`}`);
        } catch (error) {
            errorMessage(error);
        }
    };

    componentDidMount() {
        this.props.customerListStore!.resetAndRefresh();
    }

    render() {
        const { customerListStore } = this.props;
        const { configurationStore } = this.props;

        if (!customerListStore) {
            return <div>No customers found.</div>;
        }
        if (!configurationStore) {
            return <div>No Roles found.</div>;
        }

        const data = customerListStore.customerList.results;

        // const data = this.state.showArchived
        //     ? customerListStore.customerList.results
        //     : customerListStore.customerList.results.filter(c => c.dateArchived === null);

        const { customerListFilters, customerListState } = customerListStore;

        const columns = [
            {
                dataField: "firstName",
                text: "First Name",
                headerStyle: { width: "10%" },
                sort: true,
                formatter: (cellContent: any, row: Contact) =>
                    row.dateFlagged ? (
                        <>
                            <FontAwesomeIcon icon={faFlag} className="mr-2 fa-sm" style={{ color: "FFB62E" }} />
                            {row.firstName}
                        </>
                    ) : (
                        <>{row.firstName}</>
                    )
            },
            {
                dataField: "lastName",
                text: "Last Name",
                headerStyle: { width: "10%" },
                sort: true,
                order: "asc"
            },
            {
                dataField: "businessName",
                text: "Business Name",
                headerStyle: { width: "10%" },
                sort: true
            },
            {
                dataField: "adress",
                text: "Address",
                formatter: (cellContent: any, row: SimpleCustomer) => {
                    const address = row.address
                        ? (row.address.address ? row.address.address : "") +
                          ", " +
                          (row.address.city ? row.address.city : "") +
                          ", " +
                          (row.address.province ? row.address.province.name : "") +
                          " " +
                          (row.address.postalCode ? row.address.postalCode : "") +
                          " " +
                          (row.address.country ? row.address.country.name : "")
                        : " - ";
                    return address;
                }
            },
            {
                dataField: "email",
                text: "Email",
                headerStyle: { width: "20%" }
            },
            {
                dataField: "primaryPhone",
                text: "Phone",
                headerStyle: { width: "10%" }
            },
            {
                dataField: "contacts",
                text: "Contact(s)",
                headerStyle: { width: "10%" },
                formatter: (cellContent: any, row: SimpleCustomer) => {
                    if (row.type === "BUSINESS") {
                        return (row as Business).contacts.map(r => (
                            <CustomerLink
                                key={r.contact.id}
                                customer={r.contact}
                                // primaryPhone={true}
                                style={{ marginBottom: "10px" }}
                                targetBlank={true}
                            >
                                {r.title}
                            </CustomerLink>
                        ));
                    }
                    return null;
                }
            },
            {
                dataField: "id",
                text: "Actions",
                headerStyle: { width: "100px" },
                formatter: (cellContent: any, row: SimpleCustomer) => {
                    return (
                        <div className={`btn-group btn-group-toggle `}>
                            <Link
                                to={`customer/${row.id}/overview`}
                                className={`btn btn-secondary btn-sm ${row.dateArchived ? `disabled` : ``}`}
                                id={`tooltipEdit${row.id}`}
                                title="Edit Customer"
                            >
                                <FontAwesomeIcon icon={faPenSquare} />
                            </Link>
                            <UncontrolledTooltip placement="top" target={`tooltipEdit${row.id}`} delay={0} className="tooltipstrap">
                                Edit Customer
                            </UncontrolledTooltip>
                            <button
                                onClick={this.deleteCustomer(row)}
                                id={`tooltipDelete${row.id}`}
                                className={`btn btn-secondary btn-sm ${row.dateArchived ? `disabled` : ``}`}
                                disabled={row.referenced}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                            <UncontrolledTooltip placement="top" target={`tooltipDelete${row.id}`} delay={0} className="tooltipstrap">
                                Delete Customer
                            </UncontrolledTooltip>
                            {row.dateArchived ? (
                                <>
                                    <button
                                        onClick={this.handleArchiveCustomer(row, false)}
                                        id={`tooltip_archive_${row.id}`}
                                        className={`btn btn-secondary btn-sm ${row.dateArchived ? `disabled` : ``}`}
                                        // disabled={row.referenced}
                                    >
                                        <FontAwesomeIcon icon={faBoxOpen} />
                                    </button>
                                    <UncontrolledTooltip placement="top" target={`tooltip_archive_${row.id}`} delay={0} className="tooltipstrap">
                                        Unarchive Customer
                                    </UncontrolledTooltip>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={this.handleArchiveCustomer(row, true)}
                                        id={`tooltip_archive_${row.id}`}
                                        className="btn btn-secondary btn-sm"
                                        // disabled={row.referenced}
                                    >
                                        <FontAwesomeIcon icon={faArchive} />
                                    </button>
                                    <UncontrolledTooltip placement="top" target={`tooltip_archive_${row.id}`} delay={0} className="tooltipstrap">
                                        Archive Customer
                                    </UncontrolledTooltip>
                                </>
                            )}
                        </div>
                    );
                }
            }
        ];

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Customers
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-8">
                            <h1>Customer List</h1>
                        </div>
                        <div className="col-4">
                            <button
                                type="button"
                                className="btn btn-light float-right shadow"
                                data-toggle="button"
                                aria-pressed="false"
                                onClick={this.handleDialogCustomer}
                            >
                                Create New Customer
                            </button>
                        </div>
                    </div>

                    <div className="card shadow-sm mr-4">
                        <div className="card-body">
                            <form className="">
                                <div className="form-row">
                                    <div className="col-12">
                                        <div className="form-row">
                                            <div className="col-6">
                                                <div className="form-group">
                                                    <label htmlFor="firstname">Search</label>
                                                    <div className="input-group">
                                                        <div className="input-group-prepend">
                                                            <div className="input-group-text">
                                                                <FontAwesomeIcon icon={faSearch} />
                                                            </div>
                                                        </div>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            placeholder="Search..."
                                                            name="fullTextSearch"
                                                            value={customerListFilters.fullTextSearch}
                                                            onChange={this.handleSearchOnChange}
                                                            onClick={() => customerListStore.defaultFilterFullTextSearch()}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="form-group ml-5 mt-3">
                                                    <div className="form-check form-check-inline mr-3">
                                                        <label className="form-check-label d-flex align-items-center" htmlFor="rdioContactFilter">
                                                            <input
                                                                id="rdioContactFilter"
                                                                className="form-check-input mr-3"
                                                                type="radio"
                                                                name="customer-type"
                                                                value="contact"
                                                                checked={customerListStore.customerListFilters.type === "CONTACT"}
                                                                onChange={() => customerListStore.updateCustomerListFilters({ type: "CONTACT" })}
                                                            />
                                                            Contact
                                                        </label>
                                                    </div>
                                                    <div className="form-check form-check-inline mt-2">
                                                        <label
                                                            className="form-check-label ml-3 d-flex align-items-center"
                                                            htmlFor="rdioBusinessFilter"
                                                        >
                                                            <input
                                                                id="rdioBusinessFilter"
                                                                className="form-check-input mr-3"
                                                                type="radio"
                                                                name="customer-type"
                                                                value="business"
                                                                checked={customerListStore.customerListFilters.type === "BUSINESS"}
                                                                onChange={() => customerListStore.updateCustomerListFilters({ type: "BUSINESS" })}
                                                            />
                                                            Business
                                                        </label>
                                                    </div>
                                                    <div className="form-check form-check-inline mt-2">
                                                        <label className="form-check-label ml-3 d-flex align-items-center" htmlFor="rdioBothFilter">
                                                            <input
                                                                id="rdioBothFilter"
                                                                className="form-check-input mr-3"
                                                                type="radio"
                                                                name="customer-type"
                                                                value="both"
                                                                checked={customerListStore.customerListFilters.type === "BOTH"}
                                                                onChange={() => customerListStore.updateCustomerListFilters({ type: "BOTH" })}
                                                            />
                                                            Both
                                                        </label>
                                                    </div>
                                                    <div className="form-check form-check-inline ml-3">
                                                        <label
                                                            className="form-check-label ml-3 d-flex align-items-center"
                                                            htmlFor="chkIncludeArchived"
                                                        >
                                                            <input
                                                                id="chkIncludeArchived"
                                                                className="form-check-input mr-3"
                                                                type="checkbox"
                                                                name="customer-type"
                                                                value="Archive"
                                                                checked={customerListStore.customerListFilters.includeArchived}
                                                                onChange={() =>
                                                                    customerListStore.updateCustomerListFilters({
                                                                        includeArchived: !customerListStore.customerListFilters.includeArchived
                                                                    })
                                                                }
                                                            />
                                                            Include Archived Customers
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-header">Filter to those having involvement in:</div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-6">
                                                        <label htmlFor="lastname">Sale</label>
                                                        <Select
                                                            name="sales"
                                                            options={configurationStore.horseSales.map(sale => {
                                                                return {
                                                                    label: sale.name,
                                                                    value: sale.id + ""
                                                                };
                                                            })}
                                                            onChange={this.onChangeSelectSale}
                                                            isMulti={true}
                                                        />
                                                    </div>
                                                    <div className="col-6">
                                                        <label htmlFor="lastname">Role</label>
                                                        <Select
                                                            name="roles"
                                                            options={configurationStore.roles.map(role => {
                                                                return {
                                                                    label: role.friendlyName,
                                                                    value: role.searchName
                                                                };
                                                            })}
                                                            onChange={this.onChangeSelectRole}
                                                            isMulti={true}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="row mt-4">
                        <div className="col-12 ml-3">
                            Total Customers: <strong>{customerListStore.customerList.total}</strong>
                        </div>
                    </div>
                    <div className="row mt-4">
                        <div className="col-12">
                            <div className="table-responsive">
                                {
                                    <BootstrapTable
                                        keyField="id"
                                        remote={true}
                                        hover={true}
                                        bordered={false}
                                        columns={columns}
                                        bootstrap4={true}
                                        // rowEvents={{
                                        //     onClick: (e: any, row: any, rowIndex: number) => {
                                        //         e.stopPropagation();
                                        //         this.props.history.push(`/customer/${row.id}/overview`);
                                        //     }
                                        // }}
                                        // rowStyle={(row: SimpleCustomer, rowIndex: number): Partial<CSSStyleDeclaration> => {
                                        //     const style: Partial<CSSStyleDeclaration> = {};
                                        //     if (row.dateFlagged) {
                                        //         style.backgroundColor = "rgba(200,0,0,0.3)";
                                        //     }
                                        //     return style;
                                        // }}
                                        rowClasses={(row: SimpleCustomer, rowIndex: number): string => {
                                            let classes = "";
                                            if (row.dateFlagged) {
                                                classes += "row-flagged";
                                            } else if (row.dateArchived) {
                                                classes += "row-archived";
                                            }
                                            return classes;
                                        }}
                                        data={data}
                                        pagination={paginationFactory({
                                            page: customerListState.page,
                                            sizePerPage: customerListState.sizePerPage,
                                            totalSize: customerListStore.customerList.total
                                        })}
                                        onTableChange={this.tableChanged}
                                        noDataIndication={() => (
                                            <div>{customerListStore.loading ? <LoadingWidget /> : <span>No results found.</span>}</div>
                                        )}
                                    />
                                }
                            </div>
                        </div>
                        {/* This is here as a hack to make the component observe `customerListStore.loading` 
                                Otherwise the `noDataIndication` wouldn't work */}
                        {customerListStore.loading && <div />}
                    </div>
                </div>
                <NewCustomerDialog ref={el => (this.NewCustomerDialog = el)} />
            </div>
        );
    }
}

export default withRouter<Props>(CustomerList);



// WEBPACK FOOTER //
// ./src/components/customer/CustomerList.tsx